import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaFileExport, FaHandHoldingUsd, FaSearch } from "react-icons/fa";
import { Empty, PageHead } from "../../pages/work/WorkUi";
import PortalSelect from "../PortalSelect";
import billingApi, { billingError } from "./billingApi";
import { fmtDate, money, useBillingMeta } from "./BillingUi";
import { FINANCE_SECTIONS } from "../../utils/financeTerms";
import "../../pages/dm/dm.css";
import "../../pages/work/work.css";
import "./billing.css";

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

/** Finance > Payments Received: every payment recorded against client invoices, with filters and export. */
function TenantPaymentsReceived() {
  const navigate = useNavigate();
  const { meta } = useBillingMeta();
  const [filters, setFilters] = useState({ search: "", client_id: "", mode_id: "", date_from: "", date_to: "", status: "completed" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      setData(await billingApi.payments(params));
      setError("");
    } catch (err) {
      setError(billingError(err, "Could not load payments"));
    }
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  const exportCsv = () => {
    const header = ["Date", "Invoice", "Client", "Amount", "Mode", "Reference", "Status"];
    const lines = (data?.payments || []).map((p) =>
      [p.Payment_Date, p.Invoice_No, p.Client_Name, p.Amount, p.Payment_Mode, p.Reference, p.Status].map(csvCell).join(",")
    );
    const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `payments-received-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const currency = meta?.settings?.Currency;

  return (
    <div className="dmp wk bl">
      <PageHead title={FINANCE_SECTIONS.paymentsReceived.label} info="Money your clients paid against your invoices. Record a payment from the invoice itself.">
        {data?.payments?.length ? (
          <button type="button" className="dmp-btn dmp-btn--ghost" onClick={exportCsv}>
            <FaFileExport aria-hidden="true" /> Export CSV
          </button>
        ) : null}
      </PageHead>
      <div className="dmp-card">
        <div className="wk-proj-toolbar bl-filters">
          <div className="wk-search">
            <FaSearch aria-hidden="true" className="wk-search__icon" />
            <label className="wk-sr-only" htmlFor="bl-pay-search">
              Search payments
            </label>
            <input
              id="bl-pay-search"
              type="search"
              value={filters.search}
              onChange={set("search")}
              placeholder="Invoice, client or reference"
              autoComplete="off"
            />
          </div>
          <div className="bl-client-filter">
            <PortalSelect value={filters.client_id} onChange={set("client_id")} aria-label="Client" searchable>
              <option value="">All clients</option>
              {(meta?.clients || []).map((c) => (
                <option key={c.Tenant_Client_Id} value={c.Tenant_Client_Id}>
                  {c.Client_Name}
                </option>
              ))}
            </PortalSelect>
          </div>
          <div className="bl-client-filter">
            <PortalSelect value={filters.mode_id} onChange={set("mode_id")} aria-label="Payment mode">
              <option value="">All modes</option>
              {(meta?.payment_modes || []).map((m) => (
                <option key={m.Payment_Mode_Id} value={m.Payment_Mode_Id}>
                  {m.Name}
                </option>
              ))}
            </PortalSelect>
          </div>
          <label className="bl-date">
            <span>From</span>
            <input type="date" value={filters.date_from} onChange={set("date_from")} />
          </label>
          <label className="bl-date">
            <span>To</span>
            <input type="date" value={filters.date_to} onChange={set("date_to")} />
          </label>
        </div>
        <div className="wk-chip-row" role="group" aria-label="Show">
          {[
            ["completed", "Received"],
            ["cancelled", "Cancelled"],
            ["all", "All"],
          ].map(([key, label]) => (
            <button key={key} type="button" className="wk-chip" aria-pressed={filters.status === key} onClick={() => setFilters((f) => ({ ...f, status: key }))}>
              {label}
            </button>
          ))}
        </div>
        {error ? <div className="dmp-error">{error}</div> : null}
        {!data ? (
          <div className="wk-muted">Loading…</div>
        ) : data.payments.length ? (
          <>
            <table className="wk-table wk-proj-table bl-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Invoice</th>
                  <th>Client</th>
                  <th className="bl-num">Amount</th>
                  <th>Mode</th>
                  <th>Reference</th>
                </tr>
              </thead>
              <tbody>
                {data.payments.map((p) => (
                  <tr
                    key={p.Payment_Id}
                    className={`wk-proj-row${p.Status === "Cancelled" ? " is-cancelled" : ""}`}
                    onClick={() => navigate(`/portal/finance/client-invoices/${p.Invoice_Id}`)}
                  >
                    <td data-label="Date">{fmtDate(p.Payment_Date)}</td>
                    <td data-label="Invoice">
                      <Link className="wk-proj-name" to={`/portal/finance/client-invoices/${p.Invoice_Id}`} onClick={(e) => e.stopPropagation()}>
                        {p.Invoice_No}
                      </Link>
                    </td>
                    <td data-label="Client">{p.Client_Name || "—"}</td>
                    <td data-label="Amount" className="bl-num">
                      <strong>{money(p.Amount, p.Currency)}</strong>
                      {p.Status === "Cancelled" ? <div className="bl-late">Cancelled</div> : null}
                    </td>
                    <td data-label="Mode">{p.Payment_Mode || "—"}</td>
                    <td data-label="Reference">{p.Reference || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="bl-totals">
              {data.totals.count} payment(s) · Received <strong>{money(data.totals.amount, currency)}</strong>
            </div>
          </>
        ) : (
          <Empty icon={FaHandHoldingUsd} title="No payments here" />
        )}
      </div>
    </div>
  );
}

export default TenantPaymentsReceived;
